import { Sale } from '../../api/SalesAPI';
import { INSTALMENT_SUM_EPS } from '../../utils/saleInstalmentSuggestions';
import {
  computeGrossSaleSubtotal,
  computeSaleTotal,
  getLineLevelDiscountBreakdown,
  hasAnyLineLevelOffer,
} from '../../utils/saleLineItems';

type Props = {
  sale: Pick<
    Sale,
    | 'dressName'
    | 'primaryLinePrice'
    | 'primaryLineOffer'
    | 'additionalItems'
    | 'currency'
    | 'totalPrice'
  >;
};

function formatAmount(amount: number, currency: string): string {
  return `${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function PriceComparison({
  grossAmount,
  netAmount,
  currency,
  leadingSeparator = true,
}: {
  grossAmount: number;
  netAmount: number;
  currency: string;
  leadingSeparator?: boolean;
}) {
  const showOriginal = Math.abs(grossAmount - netAmount) >= INSTALMENT_SUM_EPS;

  return (
    <>
      {leadingSeparator && <span className="mx-1">·</span>}
      {showOriginal && (
        <>
          <span className="line-through">{formatAmount(grossAmount, currency)}</span>
          <span className="mx-1">·</span>
        </>
      )}
      <span className="font-semibold text-gray-900">{formatAmount(netAmount, currency)}</span>
    </>
  );
}

function SaleLineDiscountTotalSummary({ sale }: Props) {
  if (!hasAnyLineLevelOffer(sale)) {
    return null;
  }

  const breakdownLines = getLineLevelDiscountBreakdown(sale);
  const grossAmount = computeGrossSaleSubtotal(sale);
  const netAmount = sale.totalPrice ?? computeSaleTotal(sale);

  return (
    <div className="mt-1 space-y-0.5 text-xs text-gray-500">
      {breakdownLines.map((line, index) => (
        <p key={`${line.label}-${index}`}>
          {line.label}: {line.summary}
          <PriceComparison
            grossAmount={line.grossAmount}
            netAmount={line.netAmount}
            currency={sale.currency}
          />
        </p>
      ))}
      <p>
        <PriceComparison
          grossAmount={grossAmount}
          netAmount={netAmount}
          currency={sale.currency}
          leadingSeparator={false}
        />
      </p>
    </div>
  );
}

export default SaleLineDiscountTotalSummary;
